/**
 * NO BROWSER PREFILL (owner, 2026-10-20: "Please make sure browser prefill suggestion should not be there").
 *
 * ⚠ `autocomplete="off"` IS NOT ENOUGH IN CHROME. Chrome ignores it on fields it recognises as a name or an address, which is
 * why the clinic form arrived already filled while every field said "off". What Chrome does honour is a value it does not
 * recognise, so every text field and every form gets one. A MutationObserver covers fields that appear later (each screen is
 * drawn after the route loads, and dialogs open on demand), so no template has to remember to do it.
 *
 * A field that genuinely wants the browser's help can opt out with `data-allow-autofill`.
 *
 * ⚠ IT WATCHES THE APP'S OWN HOST ELEMENT, NOT THE WHOLE DOCUMENT (review MR !1 #8). Every screen, menu and dialog of this site
 * is drawn inside `<lm-root>`, so nothing outside it (a browser extension's markup, a payment provider's frame) is touched; and
 * the caller stops it when the root is destroyed.
 *
 * The same file exists in the partner portal (`src/app/no-browser-autofill.ts`); keep the two in step.
 */
export const NO_AUTOFILL_VALUE = 'lm-no-autofill';

const SKIPPED_TYPES = new Set(['checkbox', 'radio', 'file', 'hidden', 'submit', 'button', 'reset', 'range', 'color', 'image']);

const applyTo = (element: Element): void => {
    if (element.hasAttribute('data-allow-autofill')) {
        return;
    }
    if (element instanceof HTMLFormElement) {
        element.setAttribute('autocomplete', 'off');
        return;
    }
    if (element instanceof HTMLInputElement && SKIPPED_TYPES.has(element.type)) {
        return;
    }
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        if (element.getAttribute('autocomplete') !== NO_AUTOFILL_VALUE) {
            element.setAttribute('autocomplete', NO_AUTOFILL_VALUE);
        }
    }
};

const sweep = (root: ParentNode): void => {
    if (root instanceof Element) {
        applyTo(root);
    }
    root.querySelectorAll('input, textarea, form').forEach(applyTo);
};

/** Starts the rule under one element (the app's host). Returns a function that stops it. */
export const startNoBrowserAutofill = (root: HTMLElement | null | undefined): (() => void) => {
    if (!root || typeof MutationObserver === 'undefined') {
        return () => undefined;
    }
    sweep(root);
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof Element) {
                    sweep(node);
                }
            });
        }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
};
